import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DriverTrackingPage.css';
import { useLanguage } from './i18n/LanguageContext';

const coordinate = (...values) => {
  for (const value of values) {
    const number = parseFloat(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};

const mapDot = (color, kind) => L.divIcon({
  className: `tracking-map-dot${kind ? ` is-${kind}` : ''}`,
  html: `<span style="background:${color}"></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9]
});

const FitTrackingMap = ({ points }) => {
  const map = useMap();
  const serialized = JSON.stringify(points);
  useEffect(() => {
    const valid = JSON.parse(serialized).filter((point) => Array.isArray(point) && point.length === 2);
    const resize = () => map.invalidateSize();
    resize();
    if (valid.length >= 2) {
      const bounds = L.latLngBounds(valid);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [80, 80], maxZoom: 15 });
      }
    } else if (valid.length === 1) {
      map.setView(valid[0], 15);
    }
    const timer = window.setTimeout(resize, 120);
    window.addEventListener('resize', resize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', resize);
    };
  }, [map, serialized]);
  return null;
};

const DriverTrackingPage = ({ ride, onBack, onMessage, onCall }) => {
  const { t } = useLanguage();
  const [arrivalMinutes, setArrivalMinutes] = useState(4);
  const [meetTime, setMeetTime] = useState(null);

  useEffect(() => {
    // Calculate arrival time and meet time
    if (ride) {
      // Mock: Driver arriving in 4 minutes
      const now = new Date();
      const arrival = new Date(now.getTime() + arrivalMinutes * 60000);
      setMeetTime(arrival);
      
      // Update arrival minutes countdown
      const interval = setInterval(() => {
        setArrivalMinutes(prev => {
          if (prev > 0) {
            return prev - 1;
          }
          return 0;
        });
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [ride]);

  if (!ride) {
    return null;
  }

  // Extract driver information from ride data
  const driverName = ride.driverName || '';
  const driverRating = '4.95'; // Mock rating - can be from API
  const vehicleType = ride.carName || ride.vehicleType || '';
  const carNumber = ride.carNumber || '';
  const pickupLocation = ride.startLocation || '';
  const pickupLat = coordinate(ride.startLatitude, ride.pickupLatitude, ride.pickupLocation?.latitude);
  const pickupLng = coordinate(ride.startLongitude, ride.pickupLongitude, ride.pickupLocation?.longitude);
  const dropLat = coordinate(ride.endLatitude, ride.dropLatitude, ride.dropLocation?.latitude);
  const dropLng = coordinate(ride.endLongitude, ride.dropLongitude, ride.dropLocation?.longitude);
  const driverLat = coordinate(
    ride.driverLatitude,
    ride.carLatitude,
    ride.currentLatitude,
    ride.driverLocation?.latitude
  );
  const driverLng = coordinate(
    ride.driverLongitude,
    ride.carLongitude,
    ride.currentLongitude,
    ride.driverLocation?.longitude
  );
  const hasPickup = pickupLat != null && pickupLng != null;
  const hasDrop = dropLat != null && dropLng != null;
  const hasDriver = driverLat != null && driverLng != null;
  const hasMap = hasPickup || hasDrop || hasDriver;
  const mapPoints = [
    hasPickup ? [pickupLat, pickupLng] : null,
    hasDriver ? [driverLat, driverLng] : null,
    hasDrop ? [dropLat, dropLng] : null
  ].filter(Boolean);
  const mapCenter = mapPoints[0];
  
  // Generate PIN (mock - should come from API)
  const tripPIN = Array.isArray(ride.tripPIN)
    ? ride.tripPIN
    : String(ride.tripPIN || '4821').split('').slice(0, 4);

  const formatMeetTime = () => {
    if (meetTime) {
      return meetTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return '11:03 AM';
  };

  return (
    <div className="driver-tracking-page">
      {/* Back Button */}
      <button className="tracking-back-button" onClick={onBack}>
        <img
          src={process.env.PUBLIC_URL + '/images/back_button.svg'}
          alt="Back"
        />
      </button>

      {/* Status Bar */}
      <div className="tracking-status-bar">
        <div className="status-bar-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 17H19L17 19H7L5 17ZM7 7L9 3H15L17 7M7 7L5 17M17 7L19 17M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="7" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
            <circle cx="17" cy="20" r="2" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
        <div className="status-bar-text">
          <p className="status-primary">Driver arriving in {arrivalMinutes} min</p>
          <p className="status-secondary">Meet {driverName.split(' ')[0]} by {formatMeetTime()}</p>
        </div>
        <button className="status-menu-button">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
            <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
            <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
          </svg>
        </button>
      </div>

      <div className="tracking-map-area">
        {hasMap ? (
          <MapContainer
            center={mapCenter || [0, 0]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitTrackingMap points={mapPoints} />
            {hasPickup && hasDrop ? (
              <Polyline
                positions={[[pickupLat, pickupLng], [dropLat, dropLng]]}
                pathOptions={{ color: '#5b6cff', weight: 3, dashArray: '7 8' }}
              />
            ) : null}
            {hasPickup ? (
              <Marker position={[pickupLat, pickupLng]} icon={mapDot('#22a559', 'pickup')} />
            ) : null}
            {hasDrop ? (
              <Marker position={[dropLat, dropLng]} icon={mapDot('#e23d4a', 'drop')} />
            ) : null}
            {hasDriver ? (
              <Marker position={[driverLat, driverLng]} icon={mapDot('#764CA2', 'driver')} />
            ) : null}
          </MapContainer>
        ) : (
          <div className="map-placeholder">
            <p>{t('Map View')}</p>
          </div>
        )}
      </div>

      {/* Bottom Cards */}
      <div className="tracking-bottom-cards">
        {/* PIN Card */}
        <div className="pin-card">
          <span className="pin-label">{t('PIN FOR THIS TRIP')}</span>
          <div className="pin-digits">
            {tripPIN.map((digit, index) => (
              <div key={index} className="pin-digit-box">
                {digit}
              </div>
            ))}
          </div>
        </div>

        {/* Driver Details Card */}
        <div className="driver-details-card">
          {/* Pick-up Spot */}
          <div className="pickup-spot-section">
            <div className="pickup-dot"></div>
            <div className="pickup-info">
              <span className="pickup-label">{t('PICK-UP SPOT')}</span>
              <span className="pickup-location">{pickupLocation}</span>
            </div>
            <div className="pickup-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 21H21M5 21V7L12 3L19 7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 9V21M15 9V21" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
          </div>

          <div className="card-divider"></div>

          {/* Driver Information */}
          <div className="driver-info-section">
            <div className="driver-profile">
              <div className="driver-photo">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="driver-rating">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                </svg>
                <span>{driverRating}</span>
              </div>
            </div>
            <div className="driver-details">
              <h3 className="driver-name">{driverName}</h3>
              <p className="vehicle-info">
                {[vehicleType, carNumber].filter(Boolean).join(' • ')}
              </p>
            </div>
            <div className="driver-actions">
              <button className="message-button" onClick={onMessage}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('MESSAGE')}</span>
              </button>
              <button className="call-button" onClick={onCall}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7292C21.7209 20.9841 21.5573 21.2126 21.3522 21.3999C21.1472 21.5872 20.9053 21.7289 20.6419 21.8157C20.3785 21.9025 20.0995 21.9324 19.822 21.902C16.7428 21.5857 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.19 12.85C3.49997 10.2412 2.44824 7.27099 2.098 4.178C2.06758 3.90097 2.09726 3.62239 2.18489 3.35949C2.27252 3.09659 2.41598 2.85519 2.60498 2.65062C2.79399 2.44604 3.02419 2.28283 3.28058 2.17162C3.53697 2.06041 3.81399 2.00366 4.098 2.002H7.098C7.68123 1.99409 8.24251 2.16755 8.70737 2.49455C9.17224 2.82155 9.51507 3.28624 9.688 3.828L10.878 7.228C10.9543 7.45696 10.9814 7.69971 10.9573 7.93952C10.9332 8.17933 10.8584 8.41019 10.738 8.616L8.738 11.616C9.80219 13.4883 11.5117 15.1978 13.384 16.262L16.384 14.262C16.5898 14.1416 16.8207 14.0668 17.0605 14.0427C17.3003 14.0186 17.543 14.0457 17.772 14.122L21.172 15.312C21.7138 15.4849 22.1785 15.8278 22.5055 16.2926C22.8325 16.7575 23.0059 17.3188 22.998 17.902L22.998 17.902Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('CALL')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverTrackingPage;
